import { createContext, ReactNode, useMemo } from 'react';
import { useUserUUID } from 'utils/auth';
import { FeatureFlag } from '../constants';
import { useFeatureFlagsQuery } from '../query';
import { FeatureFlagsRequestData, UnleashContextState } from '../types';
import { getFeaturesDataFromList } from '../utils';

export const UnleashContext = createContext<UnleashContextState | undefined>(
  undefined
);

const UnleashProviderLogic = ({
  children,
  userId
}: {
  children: ReactNode;
  userId?: string;
}) => {
  const featureFlagsRequestData: FeatureFlagsRequestData = useMemo(
    () => ({
      features: Object.values(FeatureFlag),
      context: {
        userId
      }
    }),
    [userId]
  );

  const { isLoading, data } = useFeatureFlagsQuery(featureFlagsRequestData);

  const contextValue = useMemo(
    () => ({ features: getFeaturesDataFromList(data?.features), isLoading }),
    [data?.features, isLoading]
  );

  return (
    <UnleashContext.Provider value={contextValue}>
      {children}
    </UnleashContext.Provider>
  );
};

const AuthenticatedUnleashProvider = ({
  children
}: {
  children: ReactNode;
}) => {
  const userId = useUserUUID();

  return (
    <UnleashProviderLogic userId={userId}>{children}</UnleashProviderLogic>
  );
};

interface UnleashContextProviderProps {
  children: ReactNode;
  isPublic?: boolean;
}

export const UnleashContextProvider = ({
  children,
  isPublic = false
}: UnleashContextProviderProps) => {
  if (isPublic) {
    return (
      <UnleashProviderLogic userId={undefined}>{children}</UnleashProviderLogic>
    );
  }

  return (
    <AuthenticatedUnleashProvider>{children}</AuthenticatedUnleashProvider>
  );
};
